import { useQuery } from '@tanstack/react-query';
import { searchOpportunities } from '../services/samApi';
import { searchSemantic } from '../services/semanticApi';
import { quickScore, generateExecutiveSummary } from '../services/geminiService';
import { saveScannedContracts } from '../services/contractRepository';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Contractor } from '../types';

interface SearchParams {
    limit: number;
    postedFrom: string;
    title?: string;
    naicsCode?: string;
    typeOfSetAside?: string;
    zipCode?: string;
    radius?: string;
    city?: string;
    state?: string;
    isSemantic?: boolean;
    source?: 'sam' | 'cameron';
}

const fetchStoredAnalysesInternal = async (opportunities: any[]) => {
    if (!opportunities.length) return opportunities;
    const updates: Record<string, any> = {};

    try {
        const ids = opportunities.map(o => o.noticeId);
        const chunks = [];
        for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));

        for (const chunk of chunks) {
            const q = query(collection(db, 'contracts'), where('noticeId', 'in', chunk));
            const querySnapshot = await getDocs(q);
            querySnapshot.forEach((doc) => {
                if (doc.exists()) {
                    updates[doc.id] = doc.data();
                }
            });
        }

        return opportunities.map(opp => updates[opp.noticeId] ? { ...opp, ...updates[opp.noticeId] } : opp);
    } catch (err) {
        console.error("Error fetching stored analyses:", err);
        return opportunities;
    }
};

export const useOpportunities = (
    searchParams: SearchParams,
    selectedContractorId: string,
    contractors: Contractor[],
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: ['opportunities', searchParams, selectedContractorId],
        queryFn: async () => {
            let processedResults = [];

            // 1. Fetch raw opportunities (Source Branching)
            if (searchParams.source === 'cameron') {
                const { searchCameronOpportunities } = await import('../services/cameronCountyApi');
                processedResults = await searchCameronOpportunities({
                    keyword: searchParams.title,
                    limit: searchParams.limit
                });
            } else if (searchParams.isSemantic && searchParams.title) {
                const semanticResponse = await searchSemantic(searchParams.title, searchParams.limit);
                processedResults = semanticResponse.results;
            } else {
                const response = await searchOpportunities(searchParams);
                processedResults = response.opportunitiesData;
            }

            // 2. Score with AI if contractor is selected
            if (selectedContractorId && processedResults.length > 0) {
                const contractorProfile = contractors.find(c => c.id === selectedContractorId);
                if (contractorProfile) {
                    const scoredResults = await Promise.all(processedResults.map(async (opp: any) => {
                        // @ts-ignore
                        const { fitScore, opportunityScore, insights } = await quickScore(opp, contractorProfile);
                        // @ts-ignore
                        const narrative = generateExecutiveSummary(opp, contractorProfile, fitScore, opportunityScore, insights);
                        return { ...opp, fitScore, opportunityScore, aiSummary: narrative, insights, isAnalyzed: true };
                    }));

                    processedResults = scoredResults.sort((a: any, b: any) => (b.fitScore || 0) - (a.fitScore || 0));

                    saveScannedContracts(processedResults);
                }
            }

            // 3. Merge with Firestore Data
            const finalResults = await fetchStoredAnalysesInternal(processedResults);

            return finalResults;
        },
        enabled: enabled,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};
